import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Mail, Phone, MessageCircle, MapPin, Clock,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, MaskedLines } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { scrollToElement } from "@/lib/scrollLock";
import { Turnstile, Honeypot } from "@/components/FormGuard";
import { LocalizedLink } from "@/components/LocalizedLink";
import privacy from "@/data/privacy.json";

const inputCls =
  "w-full rounded-xl border border-linen bg-cream px-4 py-3.5 text-ink placeholder:text-stone/60 focus:border-terracotta transition-colors duration-200";

const Contatti = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: t.seo.contactPage.title, description: t.seo.contactPage.description });
  const c = t.contactPage;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const successRef = useRef(null);

  useEffect(() => {
    if (sent) scrollToElement(successRef.current);
  }, [sent]);
  const [sending, setSending] = useState(false);
  const [token, setToken] = useState("");
  const [trap, setTrap] = useState("");
  const [attempt, setAttempt] = useState(0);

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setError("");
  };

  const fail = (msg, fieldId) => {
    setError(msg);
    document.getElementById(fieldId)?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return fail(c.errors.name, "contact-name");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return fail(c.errors.email, "contact-email");
    if (form.message.trim().length < 5) return fail(c.errors.message, "contact-message");

    setSending(true);
    try {
      const resp = await fetch("/api/contatti", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, name: form.name.trim(), email: form.email.trim(), lang, website: trap, turnstileToken: token }),
      });
      if (!resp.ok) throw new Error("send failed");
      setSent(true);
    } catch {
      setError(c.errors.generic);
    } finally {
      setSending(false);
      setAttempt((n) => n + 1);
    }
  };

  return (
    <div data-testid="contatti-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40">
        <Reveal y={20}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{c.overline}</p>
        </Reveal>
        <h1 className="font-display font-medium text-5xl md:text-7xl tracking-tight mt-4" data-testid="contatti-title">
          <MaskedLines lines={[c.title]} delay={0.05} />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-2xl text-stone text-base md:text-lg leading-relaxed font-light" data-testid="contatti-sub">
            {c.sub}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-14 md:mt-20 grid lg:grid-cols-12 gap-14">
        <aside className="lg:col-span-4 min-w-0">
          <Reveal>
            <div className="lg:sticky lg:top-28 rounded-2xl border border-linen bg-card p-8" data-testid="contact-info-card">
              <h2 className="font-display text-2xl md:text-3xl">{c.infoTitle}</h2>
              <ul className="mt-6 space-y-4 text-sm text-ink/80">
                <li>
                  <a href={`mailto:${t.footer.email}`} data-testid="contact-email-link" className="flex items-center gap-3 py-1.5 hover:text-terracotta transition-colors duration-200">
                    <Mail className="h-4 w-4 text-terracotta shrink-0" />{t.footer.email}
                  </a>
                </li>
                <li>
                  <a href={`tel:${t.footer.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-3 py-1.5 hover:text-terracotta transition-colors duration-200">
                    <Phone className="h-4 w-4 text-terracotta shrink-0" />{t.footer.phone}
                  </a>
                </li>
                <li>
                  <a href={`https://wa.me/${t.footer.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-3 py-1.5 hover:text-terracotta transition-colors duration-200">
                    <MessageCircle className="h-4 w-4 text-terracotta shrink-0" />WhatsApp {t.footer.whatsapp}
                  </a>
                </li>
                <li>
                  <span data-testid="contact-address" className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-terracotta shrink-0 mt-0.5" />{t.footer.address}
                  </span>
                </li>
              </ul>
              <p className="mt-7 pt-5 border-t border-linen flex items-center gap-3 text-sm text-stone">
                <Clock className="h-4 w-4 text-terracotta shrink-0" />
                <span data-testid="contact-response-time">{c.response}</span>
              </p>
            </div>
          </Reveal>
        </aside>

        <div className="lg:col-span-8 min-w-0">
          {sent ? (
            <Reveal>
              <div ref={successRef} className="rounded-2xl border border-olive/40 bg-card p-10 md:p-14" data-testid="contact-success">
                <CheckCircle2 className="h-12 w-12 text-olive" />
                <h2 className="font-display text-4xl md:text-5xl mt-6">{c.success.title}</h2>
                <p className="mt-5 text-stone leading-relaxed max-w-xl" data-testid="contact-success-text">
                  {c.success.text.replace("{name}", form.name.split(" ")[0]).replace("{email}", form.email)}
                </p>
                <button
                  data-testid="contact-new-message-button"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", message: "" });
                  }}
                  className="mt-10 inline-flex items-center gap-3 rounded-full border border-ink/20 px-8 py-4 text-sm font-medium hover:border-terracotta hover:text-terracotta transition-colors duration-200 btn-press"
                >
                  {c.success.newMessage}
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.05}>
              <form onSubmit={submit} noValidate className="rounded-2xl border border-linen bg-card p-8 md:p-12" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.2em] text-stone font-semibold block mb-2.5">{c.form.name} *</label>
                    <input id="contact-name" autoComplete="name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={setField("name")} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.2em] text-stone font-semibold block mb-2.5">{c.form.email} *</label>
                    <input id="contact-email" type="email" autoComplete="email" data-testid="contact-email-input" className={inputCls} value={form.email} onChange={setField("email")} />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.2em] text-stone font-semibold block mb-2.5">{c.form.message} *</label>
                  <textarea id="contact-message" rows={7} data-testid="contact-message-input" className={`${inputCls} resize-none`} placeholder={c.form.messagePlaceholder} value={form.message} onChange={setField("message")} />
                </div>

                <Honeypot value={trap} onChange={setTrap} />
                <p className="mt-8 text-xs text-stone" data-testid="contact-privacy-notice">
                  {privacy[lang].formNotice}{" "}
                  <LocalizedLink to="/privacy" className="inline-block py-2.5 -my-2.5 underline underline-offset-2 hover:text-terracotta">{privacy[lang].link}</LocalizedLink>
                </p>
                <Turnstile onToken={setToken} lang={lang} resetKey={attempt} />

                {error && (
                  <div role="alert" className="mt-8 flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-5 py-4 text-sm text-destructive" data-testid="contact-error">
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <p>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group mt-6 inline-flex items-center gap-3 rounded-full bg-terracotta px-10 py-4 text-base font-medium text-cream hover:bg-terracotta-dark transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? c.form.sending : c.form.submit}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contatti;
